import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { isEmpty, get, isFunction } from 'lodash-es'
import { FormattedMessage } from 'react-intl'
import { TtIcon } from '@23/react'
import { track } from '../../../../utils/analytics'
import {
  Button as ButtonComponent,
  LoaderComponent,
  UploadComponent,
  If,
  IconComponent,
  ProgressComponent,
  TableComponent,
} from '../../../../components'
import { getLiveId } from '../../../../../global/blocs/roomInfo'

import './HandoutsFeatureComponent.scss'

const fileTypeIconMap = {
  audio: 'mp3',
  video: 'mp4',
  presentation: 'presentation',
  document: 'file-document',
  generic: 'file-attachment',
  image: 'image',
}

const onFileRemove = (file, removeAction, mode) => {
  return new Promise((resolve, reject) => {
    removeAction(file.name, mode, resolve, reject)
  })
}

const renderManageMode = (opts) => {
  const { uploadHandouts, removeHandout, uploaderStateUpdate, handouts, mode } =
    opts

  return (
    <>
      <If condition={isEmpty(handouts)}>
        <div className="empty-state">
          Here you can upload files that you want to share with your audience
          during the webinar
        </div>
      </If>
      <UploadComponent
        showUploadList
        customRequest={uploadHandouts}
        fileList={handouts}
        onChange={({ fileList }) => uploaderStateUpdate(fileList)}
        onSuccess={() => {
          track('Added Handout', {
            product: 'WebinarManagement',
          })
        }}
        onRemove={(file) => {
          onFileRemove(file, removeHandout, mode)
          track('Removed Handout', {
            product: 'WebinarManagement',
          })
        }}
        isButtonBottomAligned
      >
        <ButtonComponent
          iconConfig={{ name: 'plus', color: '1EC95B', size: [16, 16] }}
          style={{ backgroundColor: 'transparent' }}
          color="transparent"
          className="upload-video"
          ariaLabel="Add file to webinar handouts"
        >
          Add File
        </ButtonComponent>
      </UploadComponent>
    </>
  )
}

const renderViewModeFile = (handout) => {
  return (
    <div className="handout-file" key={handout.name}>
      <TtIcon className="file-icon" name={fileTypeIconMap[handout.iconName]} />
      <div className="filename">{handout.name}</div>
      <div className="buttons">
        <a
          href={handout.url}
          target="_blank"
          rel="noopener noreferrer"
          download
          aria-label={`Download ${handout.name}`}
        >
          <TtIcon className="download-icon" name="download" />
        </a>
      </div>
    </div>
  )
}

const renderProgressFile = (handout, styles) => {
  return (
    <div className="handout-file in-progress" key={handout.name}>
      <IconComponent className="file-icon" name="import-list" size={[16, 16]} />
      <div className="progress-child">
        <div className="filename">
          <FormattedMessage defaultMessage="Uploading" />
          ...
        </div>
        <ProgressComponent
          percent={handout.percent}
          status="active"
          strokeWidth={4}
          strokeColor={get(styles, 'progressBarColor')}
        />
      </div>
    </div>
  )
}

const renderViewMode = (opts) => {
  const { handouts, styles } = opts

  if (isEmpty(handouts)) {
    return null
  }

  return (
    <div className="handouts-view-list">
      {handouts.map((handout) => {
        if (handout.status === 'uploading') {
          return renderProgressFile(handout, styles)
        }

        return renderViewModeFile(handout)
      })}
    </div>
  )
}

// The LoadingWrapperComponent doesn't work because it has min-height
// and when there are no items to display it takes the same min-height
// which on Mobile looks like a big empty whole

const renderLoader = (mode) => (
  <div className="loading-wrapper">
    <LoaderComponent size={mode === 'view-table' ? 'large' : 'small'} />
  </div>
)

const getViewTableModeFile = (handout, iconColor) => (
  <div className="view-mode-file">
    <TtIcon
      className="file-icon"
      style={{ color: iconColor }}
      name={fileTypeIconMap[handout.iconName]}
    />
    <div className="filename">{handout.name}</div>
  </div>
)

const viewIcon = { name: 'chevron', size: [12, 12], className: 'view-icon' }

const viewHandout = (handout) => {
  const liveId = getLiveId()
  return (
    <ButtonComponent
      type="external_link"
      className="view-button"
      href={`/audience/filter/event_type/filedownload/event_type_object/${liveId}/event_context_file_name/${handout.name}/`}
      outlined
      iconConfig={{ ...viewIcon, color: '1EC95B' }}
      hoverIconConfig={{ ...viewIcon, color: '013622' }}
      newWindow
    >
      View
    </ButtonComponent>
  )
}

const getColumns = (iconColor) => [
  {
    title: 'Name',
    dataIndex: 'name',
    className: 'handouts-name',
    render: (_, handout) => getViewTableModeFile(handout, iconColor),
  },
  {
    title: 'Downloads',
    dataIndex: 'downloadCount',
    align: 'left',
  },
  {
    title: <span className="hidden">Handout link</span>,
    dataIndex: 'url',
    render: (_, handout) => viewHandout(handout),
  },
]

const renderViewTableMode = (opts) => {
  const { handouts, iconColor } = opts

  if (isEmpty(handouts)) {
    return <div className="no-data">You haven’t shared any files</div>
  }

  return (
    <TableComponent
      loading={false}
      columns={getColumns(iconColor)}
      data={handouts}
      pagination={false}
      rowKey={(handout) => handout.name}
      className="handouts-view-table"
    />
  )
}

const modeMap = {
  manage: renderManageMode,
  view: renderViewMode,
  'view-table': renderViewTableMode,
}

const getModeFunc = (mode, props) => {
  const modeFunc = modeMap[mode]
  if (!isFunction(modeFunc)) {
    return null
  }

  return modeFunc(props)
}

const HandoutsFeatureComponent = (props) => {
  const { isLoading, mode } = props

  return (
    <div className="handouts-feature-component">
      {isLoading ? renderLoader(mode) : getModeFunc(mode, props)}
    </div>
  )
}

HandoutsFeatureComponent.propTypes = {
  mode: PropTypes.oneOf(['manage', 'view', 'view-table']).isRequired,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  uploadHandouts: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  removeHandout: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  uploaderStateUpdate: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  handouts: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  style: PropTypes.object,
  styles: PropTypes.shape({
    progressBarColor: PropTypes.string,
  }),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  iconColor: PropTypes.string,
}

HandoutsFeatureComponent.defaultProps = {
  iconColor: '646865',
}

const mapStateToProps = (state, ownProps) => ({
  handouts: ownProps.selectors.getHandoutsForUploader(state),
})

export default connect(mapStateToProps)(HandoutsFeatureComponent)
